import { useInfiniteQuery } from "@tanstack/react-query";
import { creativeService } from "../api/services/creativeService";
import type {
  Creative,
  CreativeFilters,
  CreativesResponse,
} from "../schemas/creativeSchema";
import { useCreatives } from "./useCreatives";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type CreativesInfiniteFilters = Omit<
  CreativeFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useCreativesInfinite = (filters: CreativesInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useCreatives(undefined, { enabled: false });

  const query = useInfiniteQuery<CreativesResponse, Error>({
    queryKey: ["creatives", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      creativeService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const creatives: Creative[] =
    query.data?.pages.flatMap((p) => p.creatives) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    creatives,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
